import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { CFD, ETF, FX } from 'shared-modules/constants';
import { useGetBuilderServiceId } from 'shared-modules/services/hooks/builder';
import { useApOrManualInstrumentOptions } from 'shared-modules/hooks/symbol';
import { CfdInstrumentIcon, CustomFlag, EtfInstrumentIcon } from '../../../../../../components';
import styles from './instrumentSelectionButton.module.scss';
import InstrumentSelection from '../../InstrumentSelection'; // TODO move to under SideMenu

const DELAY_TIME = 500;

const InstrumentSelectionButton = ({ disabled, onChange }) => {
  const serviceId = useGetBuilderServiceId();
  const instrumentId = useSelector((state) => state.builder.activeCurrency);
  const instrumentOptions = useApOrManualInstrumentOptions(false, serviceId);
  const memoizedOptions = useMemo(() => instrumentOptions, [instrumentOptions]);
  const label = memoizedOptions[serviceId]
    .map((instrument) => {
      const { label: instrumentLabel, value: instrumentValue } = instrument;
      if (instrumentValue === instrumentId) {
        const [firstInstrumentId, secondInstrumentId] = instrumentLabel?.split('/') || [];
        if (firstInstrumentId && secondInstrumentId) {
          return `${firstInstrumentId} / ${secondInstrumentId}`;
        }
        return instrumentLabel;
      }
      return null;
    })
    .filter(Boolean);

  const [firstFlag, secondFlag] = instrumentId?.split('/') || [];
  const content = () => {
    if (serviceId === FX) {
      return (
        <>
          <CustomFlag className={styles.icon} country={firstFlag} />
          <CustomFlag className={styles.icon} country={secondFlag} />
        </>
      );
    }
    if (serviceId === ETF) {
      return <EtfInstrumentIcon className={styles.singleIcon} instrumentId={instrumentId} size="medium" />;
    }
    if (serviceId === CFD) {
      return <CfdInstrumentIcon className={styles.singleIcon} instrumentId={instrumentId} size="medium" />;
    }
    return null;
  };

  const [visibleInstrumentSelection, setVisibleInstrumentSelection] = useState(false);
  const hideTimerRef = useRef(null);
  const delayHide = useCallback(() => {
    hideTimerRef.current = setTimeout(() => {
      setVisibleInstrumentSelection(false);
    }, DELAY_TIME);
  }, []);
  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);
  const showSelection = useCallback(() => {
    if (!disabled) {
      setVisibleInstrumentSelection(true);
      cancelHide();
    }
  }, [disabled, cancelHide]);
  const handleChange = useCallback(
    (event) => {
      onChange(event);
      setVisibleInstrumentSelection(false);
    },
    [onChange],
  );

  return (
    <>
      <div className={styles.container} data-disabled={disabled} onMouseEnter={showSelection} onMouseLeave={delayHide}>
        <div className={styles.innerContainer}>
          {content()}
          <span className={styles.currency}>{label}</span>
        </div>
        <i className={`material-icons ${styles.arrow}`}>arrow_forward_ios</i>
      </div>
      <InstrumentSelection
        serviceId={serviceId}
        visible={visibleInstrumentSelection}
        onChange={handleChange}
        onMouseEnter={cancelHide}
        onMouseLeave={() => setVisibleInstrumentSelection(false)}
      />
    </>
  );
};

export default memo(InstrumentSelectionButton);

InstrumentSelectionButton.propTypes = {
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

InstrumentSelectionButton.defaultProps = {
  disabled: false,
};
